import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { LOADING_STATES, type LoadingState } from '../../types/redux';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import {
    organisationAPI, OrganisationApiError,
    type AdhesionParCode, type ConceptBody, type ConceptDto, type CreerInvitationBody, type CreerOrganisationBody, type CreerTravailBody,
    type DocumentBody, type DocumentDto, type DossierBody, type DossierDto, type InvitationDto, type MembreDto, type ModifierMembreBody,
    type ModifierOrganisationBody, type ModifierTravailBody, type OrganisationFiche, type OrganisationResume, type TravailDto, type VocabulaireDto,
    type AssistantOrganisationDto, type CleOrganisationBody, type PeriodeSuivi, type SuiviOrganisationDto,
} from '../../services/organisationAPI';

/**
 * Organisations (C26 SP2) : mes adhésions, l'organisation ouverte (console de
 * l'administrateur ou de l'éditeur, page réduite d'un membre) et ses contenus.
 * La porte est opaque : une fiche en 404 est `introuvable`, jamais un message.
 * Tout refus d'un geste est relayé tel quel (`refus` : code et message du serveur).
 */
export interface Refus { code: string; message: string }

export interface DepotEnCours { fait: number; total: number }

/**
 * Le lot « verser un dossier » (C26 SP4) : tenu ici, comme le lot de préparation (`indexationSlice`), pour survivre
 * au démontage de la fenêtre du dossier — la fenêtre rouverte se rattache au lot en marche et reprend la boucle.
 */
export interface LotDeVersement {
    jobId: string; perimetreId: string; organisationId: string;
    status: string; fait: number; total: number; message: string;
    /** Une tranche a échoué : la boucle s'arrête jusqu'à « Réessayer ». */
    erreur: string | null;
}

export interface OrganisationsState {
    mes: OrganisationResume[];
    status: LoadingState;
    error: string | null;
    /** Organisation ouverte : identifiant demandé, fiche (null tant que non lue ou introuvable). */
    ouverteId: string | null;
    fiche: OrganisationFiche | null;
    ficheStatus: LoadingState;
    introuvable: boolean;
    membres: MembreDto[];
    /** L'annuaire est fermé pour moi (404 d'un simple membre). */
    annuaireFerme: boolean;
    membresStatus: LoadingState;
    invitations: InvitationDto[];
    dossiers: DossierDto[];
    documents: DocumentDto[];
    travaux: TravailDto[];
    vocabulaire: VocabulaireDto | null;
    /** Le vocabulaire de chacune de mes organisations (par identifiant d'organisation), pour « Mes organisations » d'une fiche de concept (C26 SP4) — distinct de celui de la console. */
    vocabulaires: Record<string, VocabulaireDto>;
    vocabulairesStatus: LoadingState;
    contenusStatus: { dossiers: LoadingState; documents: LoadingState; travaux: LoadingState; vocabulaire: LoadingState };
    depot: DepotEnCours | null;
    lotDeVersement: LotDeVersement | null;
    refus: Refus | null;
    /** L'organisation rejointe par le dernier code accepté (« Vous avez rejoint … »). */
    rejointe: OrganisationResume | null;
    /** L'assistant de l'organisation ouverte (C26 SP5) : forfait, contingent, clé, abonnement, parrainage — administrateur. */
    assistant: AssistantOrganisationDto | null;
    assistantStatus: LoadingState;
}

export const initialOrganisationsState: OrganisationsState = {
    mes: [],
    status: LOADING_STATES.IDLE,
    error: null,
    ouverteId: null,
    fiche: null,
    ficheStatus: LOADING_STATES.IDLE,
    introuvable: false,
    membres: [],
    annuaireFerme: false,
    membresStatus: LOADING_STATES.IDLE,
    invitations: [],
    dossiers: [],
    documents: [],
    travaux: [],
    vocabulaire: null,
    vocabulaires: {},
    vocabulairesStatus: LOADING_STATES.IDLE,
    contenusStatus: { dossiers: LOADING_STATES.IDLE, documents: LOADING_STATES.IDLE, travaux: LOADING_STATES.IDLE, vocabulaire: LOADING_STATES.IDLE },
    depot: null,
    lotDeVersement: null,
    refus: null,
    rejointe: null,
    assistant: null,
    assistantStatus: LOADING_STATES.IDLE,
};

/** Lecture tolérante : les magasins de test n'ont pas toujours ce reducer. */
const stateOf = (s: RootState): OrganisationsState => (s as Partial<RootState> | undefined)?.organisations ?? initialOrganisationsState;

const JETON: Refus = { code: 'JETON', message: 'Impossible d\'obtenir le jeton d\'accès.' };

function versRefus(error: unknown): Refus {
    if (error instanceof OrganisationApiError) return { code: error.code, message: error.message };
    return { code: 'ERREUR', message: error instanceof Error && error.message ? error.message : 'Cette action n\'a pas pu être faite. Réessayez plus tard.' };
}

interface AvecInstance { instance: any }
interface Cible extends AvecInstance { id: string }

/** Un thunk qui demande le jeton puis appelle le service ; tout refus devient `{ code, message }`. */
function thunkOrganisation<Ret, Arg extends AvecInstance>(type: string, fn: (arg: Arg, token: string) => Promise<Ret>) {
    return createAsyncThunk<Ret, Arg, { state: RootState; rejectValue: Refus }>(type, async (arg, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, arg.instance);
            if (!token) return rejectWithValue(JETON);
            return await fn(arg, token);
        } catch (error) {
            return rejectWithValue(versRefus(error));
        }
    });
}

const trierParNom = (liste: OrganisationResume[]): OrganisationResume[] => [...liste].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));

// ── Suivi (C26 SP6) : lu par la section, gardé dans son état local ; un refus pose `refus` comme tout geste ──
export const loadSuivi = thunkOrganisation<SuiviOrganisationDto, Cible & { periode: PeriodeSuivi }>(
    'organisations/loadSuivi', ({ id, periode }, token) => organisationAPI.suivi(id, periode, token),
);

// ── Mes organisations ──
export const loadMesOrganisations = thunkOrganisation<OrganisationResume[], AvecInstance>(
    'organisations/loadMes', (_arg, token) => organisationAPI.mesOrganisations(token),
);
export const creerOrganisation = thunkOrganisation<OrganisationResume, AvecInstance & CreerOrganisationBody>(
    'organisations/creer', ({ nom, slug, modele, codeParrainage }, token) =>
        organisationAPI.creer({ nom, ...(slug ? { slug } : {}), modele, ...(codeParrainage?.trim() ? { codeParrainage: codeParrainage.trim() } : {}) }, token),
);
/** Un code reçu : le profil (créé s'il manquait) et, si le code est celui d'une organisation, l'adhésion. */
export const rejoindreParCode = thunkOrganisation<AdhesionParCode, AvecInstance & { code: string }>(
    'organisations/rejoindre', ({ code }, token) => organisationAPI.rejoindre(code, token),
);

// ── Fiche, cercles, groupes, réglages ──
export const loadFiche = thunkOrganisation<{ id: string; fiche: OrganisationFiche | null }, Cible>(
    'organisations/loadFiche', async ({ id }, token) => ({ id, fiche: await organisationAPI.fiche(id, token) }),
);
export const modifierOrganisation = thunkOrganisation<OrganisationFiche, Cible & { body: ModifierOrganisationBody }>(
    'organisations/modifier', ({ id, body }, token) => organisationAPI.modifier(id, body, token),
);
/** La charte acceptée (C26 SP3) : la fiche revient avec `moi.charteAAccepter` faux. */
export const accepterCharte = thunkOrganisation<OrganisationFiche, Cible>(
    'organisations/accepterCharte', ({ id }, token) => organisationAPI.accepterCharte(id, token),
);
export const reglerCercles = thunkOrganisation<OrganisationFiche, Cible & { cercles: Array<{ cle?: string; libelle: string }> }>(
    'organisations/reglerCercles', ({ id, cercles }, token) => organisationAPI.reglerCercles(id, cercles, token),
);
export const reglerGroupes = thunkOrganisation<OrganisationFiche, Cible & { groupes: Array<{ cle?: string; libelle: string }> }>(
    'organisations/reglerGroupes', ({ id, groupes }, token) => organisationAPI.reglerGroupes(id, groupes, token),
);

// ── Assistant de l'organisation (C26 SP5) ──
export const loadAssistant = thunkOrganisation<AssistantOrganisationDto, Cible>(
    'organisations/loadAssistant', ({ id }, token) => organisationAPI.assistant(id, token),
);
export const poserCleOrganisation = thunkOrganisation<AssistantOrganisationDto, Cible & { body: CleOrganisationBody }>(
    'organisations/poserCle', ({ id, body }, token) => organisationAPI.poserCle(id, body, token),
);
export const retirerCleOrganisation = thunkOrganisation<AssistantOrganisationDto, Cible>(
    'organisations/retirerCle', ({ id }, token) => organisationAPI.retirerCle(id, token),
);
/** La suppression (A11) : l'organisation sort de mes adhésions et se ferme ; `complet` faux → elle reste (relancer). */
export const supprimerOrganisation = thunkOrganisation<{ id: string; complet: boolean }, Cible & { nom: string }>(
    'organisations/supprimer', async ({ id, nom }, token) => ({ id, complet: (await organisationAPI.supprimer(id, nom, token)).complet }),
);

// ── Membres, invitations ──
export const loadMembres = thunkOrganisation<MembreDto[] | null, Cible>(
    'organisations/loadMembres', ({ id }, token) => organisationAPI.membres(id, token),
);
export const modifierMembre = thunkOrganisation<MembreDto, Cible & { userId: string; body: ModifierMembreBody }>(
    'organisations/modifierMembre', ({ id, userId, body }, token) => organisationAPI.modifierMembre(id, userId, body, token),
);
export const retirerMembre = thunkOrganisation<string, Cible & { userId: string }>(
    'organisations/retirerMembre', async ({ id, userId }, token) => { await organisationAPI.retirerMembre(id, userId, token); return userId; },
);
/** Quitter : même route que le retrait, sur soi ; l'organisation sort de mes adhésions et se ferme. */
export const quitterOrganisation = thunkOrganisation<string, Cible & { userId: string }>(
    'organisations/quitter', async ({ id, userId }, token) => { await organisationAPI.retirerMembre(id, userId, token); return id; },
);
export const loadInvitations = thunkOrganisation<InvitationDto[], Cible>(
    'organisations/loadInvitations', ({ id }, token) => organisationAPI.invitations(id, token),
);
export const creerInvitation = thunkOrganisation<InvitationDto, Cible & { body: CreerInvitationBody }>(
    'organisations/creerInvitation', ({ id, body }, token) => organisationAPI.creerInvitation(id, body, token),
);
export const revoquerInvitation = thunkOrganisation<string, Cible & { code: string }>(
    'organisations/revoquerInvitation', async ({ id, code }, token) => { await organisationAPI.revoquerInvitation(id, code, token); return code; },
);

// ── Dossiers, documents ──
export const loadDossiers = thunkOrganisation<DossierDto[], Cible>(
    'organisations/loadDossiers', ({ id }, token) => organisationAPI.dossiers(id, token),
);
export const creerDossier = thunkOrganisation<DossierDto, Cible & { body: DossierBody & { name: string } }>(
    'organisations/creerDossier', ({ id, body }, token) => organisationAPI.creerDossier(id, body, token),
);
export const modifierDossier = thunkOrganisation<DossierDto, Cible & { perimetreId: string; body: DossierBody }>(
    'organisations/modifierDossier', ({ id, perimetreId, body }, token) => organisationAPI.modifierDossier(id, perimetreId, body, token),
);
export const supprimerDossier = thunkOrganisation<string, Cible & { perimetreId: string }>(
    'organisations/supprimerDossier', async ({ id, perimetreId }, token) => { await organisationAPI.supprimerDossier(id, perimetreId, token); return perimetreId; },
);
export const loadDocuments = thunkOrganisation<DocumentDto[], Cible & { dossier?: string }>(
    'organisations/loadDocuments', ({ id, dossier }, token) => organisationAPI.documents(id, token, dossier),
);
export const modifierDocument = thunkOrganisation<DocumentDto, Cible & { fileId: string; body: DocumentBody }>(
    'organisations/modifierDocument', ({ id, fileId, body }, token) => organisationAPI.modifierDocument(id, fileId, body, token),
);
export const supprimerDocument = thunkOrganisation<string, Cible & { fileId: string }>(
    'organisations/supprimerDocument', async ({ id, fileId }, token) => { await organisationAPI.supprimerDocument(id, fileId, token); return fileId; },
);
/**
 * Dépôt par lot (spec § 0.7) : N adresses d'envoi, puis pour chaque fichier les octets
 * sur le stockage et le « complete » ; le jeton est redemandé avant chaque appel serveur.
 * Les documents déposés sont rendus (leur préparation est lancée ensuite par `useIndexationLot`).
 */
export const deposerDocuments = createAsyncThunk<DocumentDto[], Cible & { perimetreId: string; fichiers: File[] }, { state: RootState; rejectValue: Refus }>(
    'organisations/deposer',
    async ({ instance, id, perimetreId, fichiers }, { dispatch, rejectWithValue }) => {
        try {
            const jeton = async (): Promise<string> => {
                const t = await getTokenRedirect(tokenRequest, instance);
                if (!t) throw new OrganisationApiError(JETON.code, JETON.message);
                return t;
            };
            dispatch(depotProgression({ fait: 0, total: fichiers.length }));
            const prepares = await organisationAPI.preparerDepot(
                id, perimetreId, fichiers.map(f => ({ fileName: f.name, ...(f.type ? { mimeType: f.type } : {}), ...(f.size > 0 ? { size: f.size } : {}) })), await jeton(),
            );
            const deposes: DocumentDto[] = [];
            for (let i = 0; i < prepares.length; i++) {
                await organisationAPI.envoyerOctets(prepares[i].uploadUrl, fichiers[i]);
                deposes.push(await organisationAPI.terminerDepot(id, prepares[i].fileId, await jeton()));
                dispatch(depotProgression({ fait: i + 1, total: fichiers.length }));
            }
            return deposes;
        } catch (error) {
            return rejectWithValue(versRefus(error));
        }
    },
);

// ── Travaux ──
export const loadTravaux = thunkOrganisation<TravailDto[], Cible & { dossier?: string }>(
    'organisations/loadTravaux', ({ id, dossier }, token) => organisationAPI.travaux(id, token, dossier),
);
export const creerTravail = thunkOrganisation<TravailDto, Cible & { body: CreerTravailBody }>(
    'organisations/creerTravail', ({ id, body }, token) => organisationAPI.creerTravail(id, body, token),
);
export const modifierTravail = thunkOrganisation<TravailDto, Cible & { noteId: string; body: ModifierTravailBody }>(
    'organisations/modifierTravail', ({ id, noteId, body }, token) => organisationAPI.modifierTravail(id, noteId, body, token),
);
export const supprimerTravail = thunkOrganisation<string, Cible & { noteId: string }>(
    'organisations/supprimerTravail', async ({ id, noteId }, token) => { await organisationAPI.supprimerTravail(id, noteId, token); return noteId; },
);

// ── Vocabulaire ──
export const loadVocabulaire = thunkOrganisation<VocabulaireDto, Cible>(
    'organisations/loadVocabulaire', ({ id }, token) => organisationAPI.vocabulaire(id, token),
);
export const creerConcept = thunkOrganisation<ConceptDto, Cible & { body: ConceptBody & { cle: string } }>(
    'organisations/creerConcept', ({ id, body }, token) => organisationAPI.creerConcept(id, body, token),
);
export const modifierConcept = thunkOrganisation<ConceptDto, Cible & { entreeId: string; body: ConceptBody }>(
    'organisations/modifierConcept', ({ id, entreeId, body }, token) => organisationAPI.modifierConcept(id, entreeId, body, token),
);
export const supprimerConcept = thunkOrganisation<string, Cible & { entreeId: string }>(
    'organisations/supprimerConcept', async ({ id, entreeId }, token) => { await organisationAPI.supprimerConcept(id, entreeId, token); return entreeId; },
);

/**
 * Le vocabulaire de toutes mes adhésions (C26 SP4), lu une fois par session ; une organisation
 * dont le vocabulaire ne se lit pas est laissée de côté (le bloc ne dit rien d'elle).
 */
export const chargerVocabulaires = createAsyncThunk<Record<string, VocabulaireDto>, AvecInstance, { state: RootState; rejectValue: Refus }>(
    'organisations/chargerVocabulaires',
    async ({ instance }, { getState, rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue(JETON);
            const resultat: Record<string, VocabulaireDto> = {};
            await Promise.all(stateOf(getState()).mes.map(async (o) => {
                try { resultat[o.id] = await organisationAPI.vocabulaire(o.id, token); } catch { /* laissée de côté */ }
            }));
            return resultat;
        } catch (error) {
            return rejectWithValue(versRefus(error));
        }
    },
);

const remplacer = <T extends { id: string }>(liste: T[], item: T): T[] => (liste.some(x => x.id === item.id) ? liste.map(x => (x.id === item.id ? item : x)) : [item, ...liste]);

/** Les contenus et l'annuaire repartent de zéro à chaque organisation ouverte. */
function viderOuverte(state: OrganisationsState): void {
    state.fiche = null;
    state.introuvable = false;
    state.membres = [];
    state.annuaireFerme = false;
    state.membresStatus = LOADING_STATES.IDLE;
    state.invitations = [];
    state.dossiers = [];
    state.documents = [];
    state.travaux = [];
    state.vocabulaire = null;
    state.contenusStatus = { ...initialOrganisationsState.contenusStatus };
    state.depot = null;
    state.assistant = null;
    state.assistantStatus = LOADING_STATES.IDLE;
}

const organisationsSlice = createSlice({
    name: 'organisations',
    initialState: initialOrganisationsState,
    reducers: {
        fermerOrganisation(state) {
            state.ouverteId = null;
            state.ficheStatus = LOADING_STATES.IDLE;
            viderOuverte(state);
        },
        effacerRefus(state) {
            state.refus = null;
        },
        effacerRejointe(state) {
            state.rejointe = null;
        },
        lotVersementDemarre(state, action: PayloadAction<{ jobId: string; perimetreId: string; organisationId: string }>) {
            state.lotDeVersement = { ...action.payload, status: 'generating', fait: 0, total: 0, message: '', erreur: null };
        },
        lotVersementMisAJour(state, action: PayloadAction<{ jobId: string; status: string; fait: number; total: number; message?: string }>) {
            const lot = state.lotDeVersement;
            if (!lot || lot.jobId !== action.payload.jobId) return;
            lot.status = action.payload.status; lot.fait = action.payload.fait; lot.total = action.payload.total; lot.message = action.payload.message ?? '';
        },
        lotVersementEnErreur(state, action: PayloadAction<{ jobId: string; message: string }>) {
            if (state.lotDeVersement?.jobId === action.payload.jobId) state.lotDeVersement.erreur = action.payload.message;
        },
        lotVersementReprend(state) { if (state.lotDeVersement) state.lotDeVersement.erreur = null; },
        lotVersementOublie(state) { state.lotDeVersement = null; },
        depotProgression(state, action: PayloadAction<DepotEnCours>) {
            state.depot = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadMesOrganisations.pending, (state) => { state.status = LOADING_STATES.LOADING; state.error = null; })
            .addCase(loadMesOrganisations.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.mes = trierParNom(action.payload);
                // Mes adhésions ont pu changer : « Mes organisations » d'une fiche relira les vocabulaires.
                state.vocabulairesStatus = LOADING_STATES.IDLE;
            })
            .addCase(loadMesOrganisations.rejected, (state, action) => { state.status = LOADING_STATES.FAILED; state.error = action.payload?.message ?? 'Vos organisations ne peuvent pas être lues pour l\'instant.'; })
            .addCase(creerOrganisation.fulfilled, (state, action) => { state.mes = trierParNom(remplacer(state.mes, action.payload)); })
            .addCase(rejoindreParCode.fulfilled, (state, action) => {
                const organisation = action.payload.organisation;
                state.rejointe = organisation ?? null;
                if (organisation) state.mes = trierParNom(remplacer(state.mes, organisation));
                state.vocabulairesStatus = LOADING_STATES.IDLE;
            })

            .addCase(loadFiche.pending, (state, action) => {
                if (state.ouverteId !== action.meta.arg.id) { state.ouverteId = action.meta.arg.id; viderOuverte(state); }
                state.ficheStatus = LOADING_STATES.LOADING;
                state.introuvable = false;
            })
            .addCase(loadFiche.fulfilled, (state, action) => {
                if (state.ouverteId !== action.payload.id) return;
                state.ficheStatus = LOADING_STATES.SUCCEEDED;
                state.fiche = action.payload.fiche;
                state.introuvable = action.payload.fiche === null;
                if (action.payload.fiche) {
                    const { id, slug, nom, roles, cercle, cercleLibelle } = action.payload.fiche;
                    state.mes = trierParNom(remplacer(state.mes, { id, slug, nom, roles, cercle, cercleLibelle }));
                }
            })
            .addCase(loadFiche.rejected, (state) => { state.ficheStatus = LOADING_STATES.FAILED; })

            .addCase(loadMembres.pending, (state) => { state.membresStatus = LOADING_STATES.LOADING; })
            .addCase(loadMembres.fulfilled, (state, action) => {
                state.membresStatus = LOADING_STATES.SUCCEEDED;
                state.annuaireFerme = action.payload === null;
                state.membres = action.payload ?? [];
            })
            .addCase(loadMembres.rejected, (state) => { state.membresStatus = LOADING_STATES.FAILED; })
            .addCase(modifierMembre.fulfilled, (state, action) => {
                state.membres = state.membres.map(m => (m.userId === action.payload.userId ? action.payload : m));
            })
            .addCase(retirerMembre.fulfilled, (state, action) => {
                state.membres = state.membres.filter(m => m.userId !== action.payload);
                if (state.fiche?.compte) state.fiche.compte.membres = Math.max(0, state.fiche.compte.membres - 1);
            })
            .addCase(loadAssistant.pending, (state) => { state.assistantStatus = LOADING_STATES.LOADING; })
            .addCase(loadAssistant.fulfilled, (state, action) => { state.assistantStatus = LOADING_STATES.SUCCEEDED; state.assistant = action.payload; })
            .addCase(loadAssistant.rejected, (state) => { state.assistantStatus = LOADING_STATES.FAILED; })
            .addCase(poserCleOrganisation.fulfilled, (state, action) => { state.assistant = action.payload; })
            .addCase(retirerCleOrganisation.fulfilled, (state, action) => { state.assistant = action.payload; })
            .addCase(supprimerOrganisation.fulfilled, (state, action) => {
                if (!action.payload.complet) return;
                state.mes = state.mes.filter(o => o.id !== action.payload.id);
                delete state.vocabulaires[action.payload.id];
                state.vocabulairesStatus = LOADING_STATES.IDLE;
                if (state.ouverteId === action.payload.id) { state.ouverteId = null; state.ficheStatus = LOADING_STATES.IDLE; viderOuverte(state); }
            })
            .addCase(quitterOrganisation.fulfilled, (state, action) => {
                state.mes = state.mes.filter(o => o.id !== action.payload);
                delete state.vocabulaires[action.payload];
                state.vocabulairesStatus = LOADING_STATES.IDLE;
                if (state.ouverteId === action.payload) { state.ouverteId = null; state.ficheStatus = LOADING_STATES.IDLE; viderOuverte(state); }
            })
            .addCase(loadInvitations.fulfilled, (state, action) => { state.invitations = action.payload; })
            .addCase(creerInvitation.fulfilled, (state, action) => {
                state.invitations = [action.payload, ...state.invitations.filter(i => i.code !== action.payload.code)];
                if (state.fiche?.compte) state.fiche.compte.invitationsEnCours += 1;
            })
            .addCase(revoquerInvitation.fulfilled, (state, action) => {
                state.invitations = state.invitations.filter(i => i.code !== action.payload);
                if (state.fiche?.compte) state.fiche.compte.invitationsEnCours = Math.max(0, state.fiche.compte.invitationsEnCours - 1);
            })

            .addCase(loadDossiers.pending, (state) => { state.contenusStatus.dossiers = LOADING_STATES.LOADING; })
            .addCase(loadDossiers.fulfilled, (state, action) => { state.contenusStatus.dossiers = LOADING_STATES.SUCCEEDED; state.dossiers = action.payload; })
            .addCase(loadDossiers.rejected, (state) => { state.contenusStatus.dossiers = LOADING_STATES.FAILED; })
            .addCase(creerDossier.fulfilled, (state, action) => { state.dossiers = [...state.dossiers, action.payload]; })
            .addCase(modifierDossier.fulfilled, (state, action) => { state.dossiers = state.dossiers.map(d => (d.id === action.payload.id ? action.payload : d)); })
            .addCase(supprimerDossier.fulfilled, (state, action) => {
                state.dossiers = state.dossiers.filter(d => d.id !== action.payload);
                state.documents = state.documents.filter(d => !d.perimetreIds.includes(action.payload));
            })
            .addCase(loadDocuments.pending, (state) => { state.contenusStatus.documents = LOADING_STATES.LOADING; })
            .addCase(loadDocuments.fulfilled, (state, action) => { state.contenusStatus.documents = LOADING_STATES.SUCCEEDED; state.documents = action.payload; })
            .addCase(loadDocuments.rejected, (state) => { state.contenusStatus.documents = LOADING_STATES.FAILED; })
            .addCase(modifierDocument.fulfilled, (state, action) => { state.documents = state.documents.map(d => (d.id === action.payload.id ? action.payload : d)); })
            .addCase(supprimerDocument.fulfilled, (state, action) => { state.documents = state.documents.filter(d => d.id !== action.payload); })
            .addCase(deposerDocuments.fulfilled, (state, action) => {
                state.depot = null;
                state.documents = [...action.payload, ...state.documents.filter(d => !action.payload.some(n => n.id === d.id))];
            })
            .addCase(deposerDocuments.rejected, (state) => { state.depot = null; })

            .addCase(loadTravaux.pending, (state) => { state.contenusStatus.travaux = LOADING_STATES.LOADING; })
            .addCase(loadTravaux.fulfilled, (state, action) => { state.contenusStatus.travaux = LOADING_STATES.SUCCEEDED; state.travaux = action.payload; })
            .addCase(loadTravaux.rejected, (state) => { state.contenusStatus.travaux = LOADING_STATES.FAILED; })
            .addCase(creerTravail.fulfilled, (state, action) => { state.travaux = [action.payload, ...state.travaux]; })
            .addCase(modifierTravail.fulfilled, (state, action) => { state.travaux = state.travaux.map(t => (t.id === action.payload.id ? action.payload : t)); })
            .addCase(supprimerTravail.fulfilled, (state, action) => { state.travaux = state.travaux.filter(t => t.id !== action.payload); })

            .addCase(loadVocabulaire.pending, (state) => { state.contenusStatus.vocabulaire = LOADING_STATES.LOADING; })
            .addCase(loadVocabulaire.fulfilled, (state, action) => {
                state.contenusStatus.vocabulaire = LOADING_STATES.SUCCEEDED;
                state.vocabulaire = action.payload;
                // La console vient de relire ce vocabulaire : « Mes organisations » d'une fiche le reprend.
                state.vocabulaires[action.meta.arg.id] = action.payload;
            })
            .addCase(loadVocabulaire.rejected, (state) => { state.contenusStatus.vocabulaire = LOADING_STATES.FAILED; })
            .addCase(creerConcept.fulfilled, (state, action) => {
                // Le vocabulaire a changé : « Mes organisations » d'une fiche le relira.
                state.vocabulairesStatus = LOADING_STATES.IDLE;
                if (state.vocabulaire) state.vocabulaire.concepts = [...state.vocabulaire.concepts, action.payload].sort((a, b) => a.cle.localeCompare(b.cle, 'fr'));
            })
            .addCase(modifierConcept.fulfilled, (state, action) => {
                // Le vocabulaire a changé : « Mes organisations » d'une fiche le relira.
                state.vocabulairesStatus = LOADING_STATES.IDLE;
                if (state.vocabulaire) state.vocabulaire.concepts = state.vocabulaire.concepts.map(c => (c.id === action.payload.id ? action.payload : c));
            })
            .addCase(supprimerConcept.fulfilled, (state, action) => {
                // Le vocabulaire a changé : « Mes organisations » d'une fiche le relira.
                state.vocabulairesStatus = LOADING_STATES.IDLE;
                if (state.vocabulaire) state.vocabulaire.concepts = state.vocabulaire.concepts.filter(c => c.id !== action.payload);
            })

            .addCase(chargerVocabulaires.pending, (state) => { state.vocabulairesStatus = LOADING_STATES.LOADING; })
            .addCase(chargerVocabulaires.fulfilled, (state, action) => { state.vocabulairesStatus = LOADING_STATES.SUCCEEDED; state.vocabulaires = action.payload; })
            .addCase(chargerVocabulaires.rejected, (state) => { state.vocabulairesStatus = LOADING_STATES.FAILED; })

            // La fiche relue après un geste qui la change (réglages, cercles, groupes).
            .addMatcher(
                (action): action is PayloadAction<OrganisationFiche> =>
                    [modifierOrganisation.fulfilled.type, reglerCercles.fulfilled.type, reglerGroupes.fulfilled.type, accepterCharte.fulfilled.type].includes(action.type),
                (state, action) => {
                    if (state.ouverteId !== action.payload.id) return;
                    state.fiche = action.payload;
                    const { id, slug, nom, roles, cercle, cercleLibelle } = action.payload;
                    state.mes = trierParNom(remplacer(state.mes, { id, slug, nom, roles, cercle, cercleLibelle }));
                },
            )
            // Tout geste refusé (hors lectures de la fiche et de l'annuaire, qui ont leur 404 silencieux) pose `refus`.
            .addMatcher(
                (action): action is PayloadAction<Refus | undefined> => typeof action.type === 'string' && action.type.startsWith('organisations/') && action.type.endsWith('/rejected'),
                (state, action) => {
                    if (action.payload) state.refus = action.payload;
                },
            )
            .addMatcher(
                (action): action is PayloadAction<unknown> => typeof action.type === 'string' && action.type.startsWith('organisations/') && action.type.endsWith('/pending'),
                (state) => { state.refus = null; },
            );
    },
});

export const {
    fermerOrganisation, effacerRefus, effacerRejointe, depotProgression,
    lotVersementDemarre, lotVersementMisAJour, lotVersementEnErreur, lotVersementReprend, lotVersementOublie,
} = organisationsSlice.actions;

export const selectLotDeVersement = (s: RootState): LotDeVersement | null => stateOf(s).lotDeVersement ?? null;
export const lotEnMarche = (lot: LotDeVersement | null | undefined): boolean => !!lot && (lot.status === 'generating' || lot.status === 'applying');

export const selectMesOrganisations = (s: RootState): OrganisationResume[] => stateOf(s).mes;
export const selectOrganisationsStatus = (s: RootState): LoadingState => stateOf(s).status;
export const selectOrganisationsError = (s: RootState): string | null => stateOf(s).error;
export const selectOrganisationOuverteId = (s: RootState): string | null => stateOf(s).ouverteId;
export const selectFiche = (s: RootState): OrganisationFiche | null => stateOf(s).fiche;
export const selectFicheStatus = (s: RootState): LoadingState => stateOf(s).ficheStatus;
export const selectFicheIntrouvable = (s: RootState): boolean => stateOf(s).introuvable;
export const selectMembres = (s: RootState): MembreDto[] => stateOf(s).membres;
export const selectAnnuaireFerme = (s: RootState): boolean => stateOf(s).annuaireFerme;
export const selectMembresStatus = (s: RootState): LoadingState => stateOf(s).membresStatus;
export const selectInvitations = (s: RootState): InvitationDto[] => stateOf(s).invitations;
export const selectDossiers = (s: RootState): DossierDto[] => stateOf(s).dossiers;
export const selectDocuments = (s: RootState): DocumentDto[] => stateOf(s).documents;
export const selectTravaux = (s: RootState): TravailDto[] => stateOf(s).travaux;
export const selectVocabulaire = (s: RootState): VocabulaireDto | null => stateOf(s).vocabulaire;
export const selectVocabulaires = (s: RootState): Record<string, VocabulaireDto> => stateOf(s).vocabulaires;
export const selectVocabulairesStatus = (s: RootState): LoadingState => stateOf(s).vocabulairesStatus;
export const selectContenusStatus = (s: RootState): OrganisationsState['contenusStatus'] => stateOf(s).contenusStatus;
export const selectDepot = (s: RootState): DepotEnCours | null => stateOf(s).depot;
export const selectRefus = (s: RootState): Refus | null => stateOf(s).refus;
export const selectRejointe = (s: RootState): OrganisationResume | null => stateOf(s).rejointe;
export const selectAssistantOrganisation = (s: RootState): AssistantOrganisationDto | null => stateOf(s).assistant;
export const selectAssistantOrganisationStatus = (s: RootState): LoadingState => stateOf(s).assistantStatus;

export default organisationsSlice.reducer;
